export function latLngPathToArray(path) {
  if (!path || !path.getArray) return [];
  return path.getArray().map((latLng) => ({
    lat: latLng.lat(),
    lng: latLng.lng(),
  }));
}

function haversineDistanceMeters(pointA, pointB) {
  const R = 6371000;
  const lat1 = (pointA.lat * Math.PI) / 180;
  const lat2 = (pointB.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((pointB.lng - pointA.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toLatLngArray(points, maps) {
  if (maps && maps.LatLng) {
    return points.map((point) => new maps.LatLng(point.lat, point.lng));
  }
  return points;
}

export function computePointsLengthMeters(points, maps) {
  if (!points || points.length < 2) return 0;
  if (maps && maps.geometry && maps.geometry.spherical) {
    return maps.geometry.spherical.computeLength(toLatLngArray(points, maps));
  }
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += haversineDistanceMeters(points[index - 1], points[index]);
  }
  return total;
}

export function computePointsAreaHa(points, maps) {
  if (!points || points.length < 3) return 0;
  if (maps && maps.geometry && maps.geometry.spherical) {
    return (
      maps.geometry.spherical.computeArea(toLatLngArray(points, maps)) / 10000
    );
  }

  const lats = points.map((point) => point.lat);
  const lons = points.map((point) => point.lng);
  const avgLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
  const mPerDegLat = 110574.0;
  const mPerDegLon = 111320.0 * Math.abs(Math.cos((avgLat * Math.PI) / 180));
  const ptsM = points.map((point) => ({
    x: point.lng * mPerDegLon,
    y: point.lat * mPerDegLat,
  }));

  let area = 0;
  for (let index = 0; index < ptsM.length; index += 1) {
    const next = (index + 1) % ptsM.length;
    area += ptsM[index].x * ptsM[next].y - ptsM[next].x * ptsM[index].y;
  }

  return Math.abs(area / 2) / 10000;
}

export function computePointsPerimeterM(points, maps) {
  if (!points || points.length < 3) {
    return computePointsLengthMeters(points, maps);
  }
  return computePointsLengthMeters(closePolygonRing(points), maps);
}

export function computePathLengthMeters(maps, path) {
  if (!path) return 0;
  if (maps && maps.geometry && maps.geometry.spherical && path.getArray) {
    return maps.geometry.spherical.computeLength(path);
  }
  return computePointsLengthMeters(latLngPathToArray(path), maps);
}

export function computePolygonAreaHa(maps, path) {
  if (!path) return 0;
  if (maps && maps.geometry && maps.geometry.spherical && path.getArray) {
    return maps.geometry.spherical.computeArea(path) / 10000;
  }
  return computePointsAreaHa(latLngPathToArray(path), maps);
}

export function computePolygonPerimeterM(maps, path) {
  if (!path) return 0;
  if (maps && maps.geometry && maps.geometry.spherical && path.getArray) {
    return maps.geometry.spherical.computeLength(path);
  }
  return computePointsPerimeterM(latLngPathToArray(path), maps);
}

export function formatDistanceM(meters) {
  const value = Number(meters) || 0;
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)} km`;
  }
  return `${value.toFixed(1)} m`;
}

export function formatAreaHa(hectares) {
  const value = Number(hectares) || 0;
  return `${value.toFixed(2)} ha`;
}

export function closePolygonRing(points) {
  if (!points || points.length === 0) return [];
  const ring = points.slice();
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first.lat !== last.lat || first.lng !== last.lng) {
    ring.push({ ...first });
  }
  return ring;
}

function ringSignedArea(points) {
  const ring = ringToEditablePoints(closePolygonRing(points));
  if (ring.length < 3) return 0;
  let area = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const next = (index + 1) % ring.length;
    area += ring[index].lng * ring[next].lat;
    area -= ring[next].lng * ring[index].lat;
  }
  return area / 2;
}

/** Anel externo CCW; recortes internos CW (GeoJSON / PostGIS / Google Maps). */
export function normalizePolygonRings(outerPoints, holeRings = []) {
  const outerOpen = ringToEditablePoints(closePolygonRing(outerPoints || []));
  if (outerOpen.length >= 3 && ringSignedArea(outerOpen) < 0) {
    outerOpen.reverse();
  }

  const outerWindingPositive = ringSignedArea(outerOpen) > 0;
  const holes = (holeRings || []).map((hole) => {
    const holeOpen = ringToEditablePoints(closePolygonRing(hole || []));
    if (holeOpen.length < 3) return holeOpen;
    const holeWindingPositive = ringSignedArea(holeOpen) > 0;
    if (holeWindingPositive === outerWindingPositive) {
      holeOpen.reverse();
    }
    return holeOpen;
  });

  return { outer: outerOpen, holes };
}

export function pathToLineWkt(points) {
  const coords = points.map((point) => `${point.lng} ${point.lat}`).join(", ");
  return `SRID=4326;LINESTRING (${coords})`;
}

export function pathToPolygonWkt(points) {
  const ring = closePolygonRing(points);
  const coords = ring.map((point) => `${point.lng} ${point.lat}`).join(", ");
  return `SRID=4326;POLYGON ((${coords}))`;
}

export function computePolygonWithHolesAreaHa(outerPoints, holeRings, maps) {
  const outerHa = computePointsAreaHa(outerPoints, maps);
  const holesHa = (holeRings || []).reduce(
    (sum, ring) => sum + computePointsAreaHa(ring, maps),
    0
  );
  return Math.max(0, outerHa - holesHa);
}

export function pathToPolygonWithHolesWkt(outerPoints, holeRings) {
  const { outer, holes } = normalizePolygonRings(outerPoints, holeRings);
  const outerCoords = closePolygonRing(outer)
    .map((point) => `${point.lng} ${point.lat}`)
    .join(", ");
  const holeParts = holes
    .filter((hole) => hole.length >= 3)
    .map((hole) => {
      const coords = closePolygonRing(hole)
        .map((point) => `${point.lng} ${point.lat}`)
        .join(", ");
      return `(${coords})`;
    });
  if (!holeParts.length) {
    return pathToPolygonWkt(outer);
  }
  return `SRID=4326;POLYGON ((${outerCoords}), ${holeParts.join(", ")})`;
}

export function buildPolygonShapeMetrics(outerPoints, holeRings, maps) {
  return {
    areaHa: computePolygonWithHolesAreaHa(outerPoints, holeRings, maps),
    perimeterM: computePointsPerimeterM(outerPoints, maps),
    polygonWkt: pathToPolygonWithHolesWkt(outerPoints, holeRings),
  };
}

function holeCentroid(hole) {
  const points = ringToEditablePoints(closePolygonRing(hole || []));
  if (!points.length) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  };
}

export function validateHolesInsideOuter(outerPoints, holeRings) {
  const outer = ringToEditablePoints(closePolygonRing(outerPoints || []));
  if (outer.length < 3) {
    return "O contorno do talhão precisa de pelo menos 3 pontos.";
  }

  const invalidIndex = (holeRings || []).findIndex((hole) => {
    if (!hole || hole.length < 3) return true;
    const ring = ringToEditablePoints(closePolygonRing(hole));
    return ring.some((point) => !pointInPolygon(point, outer));
  });

  if (invalidIndex >= 0) {
    return `O recorte ${invalidIndex + 1} precisa ficar totalmente dentro do talhão.`;
  }

  return null;
}

export function countWktInteriorRings(polygonWkt) {
  if (!polygonWkt || typeof polygonWkt !== "string") return 0;
  const match = polygonWkt.match(/POLYGON\s*\(\((.*)\)\)/i);
  if (!match) return 0;
  const body = match[1];
  let depth = 0;
  let ringStarts = 0;
  for (let index = 0; index < body.length; index += 1) {
    const char = body[index];
    if (char === "(") {
      if (depth === 0) ringStarts += 1;
      depth += 1;
    } else if (char === ")") {
      depth -= 1;
    }
  }
  return Math.max(0, ringStarts - 1);
}

export function parseLandplotPolygon(landplot) {
  if (!landplot) return { outer: [], holes: [] };
  try {
    const geometry = JSON.parse(landplot.geomap);
    const rings = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    const toPoints = (ring) =>
      (ring || []).map((item) => ({
        lat: item[1],
        lng: item[0],
      }));
    return {
      outer: toPoints(rings[0]),
      holes: rings.slice(1).map(toPoints),
    };
  } catch (error) {
    return { outer: [], holes: [] };
  }
}

export function parseLandplotRing(landplot) {
  return parseLandplotPolygon(landplot).outer;
}

/** Remove vértice de fechamento duplicado para edição no mapa. */
export function ringToEditablePoints(ring) {
  if (!ring || ring.length < 2) return ring || [];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first.lat === last.lat && first.lng === last.lng) {
    return ring.slice(0, -1);
  }
  return ring;
}

export const LAND_PLOT_MAP_STYLE = {
  fillColor: "#A5D6A7",
  fillOpacity: 0.2,
  strokeColor: "#81C784",
  strokeOpacity: 0.75,
  strokeWeight: 2,
};

export const LAND_PLOT_MAP_STYLE_HIGHLIGHTED = {
  fillColor: "#A5D6A7",
  fillOpacity: 0.32,
  strokeColor: "#FFD600",
  strokeOpacity: 1,
  strokeWeight: 4,
};

export function getLandPlotMapStyle(highlighted = false) {
  return highlighted ? LAND_PLOT_MAP_STYLE_HIGHLIGHTED : LAND_PLOT_MAP_STYLE;
}

const CROP_COLORS = [
  "#2E7D32",
  "#558B2F",
  "#F9A825",
  "#EF6C00",
  "#6A1B9A",
  "#00838F",
  "#AD1457",
  "#4527A0",
  "#283593",
  "#00695C",
];

export function cropColorFromName(name, indexFallback = 0) {
  const label = (name || "").trim();
  if (!label) {
    return CROP_COLORS[indexFallback % CROP_COLORS.length];
  }
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CROP_COLORS[Math.abs(hash) % CROP_COLORS.length];
}

export function cropDisplayName(landplot) {
  if (!landplot) return "";
  return (landplot.crop_name || landplot.name || "").trim();
}

export function formatPlotNameHectares(areaHa) {
  const hectares = Number(areaHa);
  if (!Number.isFinite(hectares) || hectares < 0) {
    return "0";
  }
  return String(Math.round(hectares));
}

export function buildPlotName(baseName, areaHa) {
  const base = (baseName || "").trim();
  const formattedHa = formatPlotNameHectares(areaHa);
  if (!base) {
    return `${formattedHa} ha`;
  }
  return `${base} — ${formattedHa} ha`;
}

export function getPlotBaseName(landplot) {
  if (!landplot) return "";
  if (landplot.crop_name && landplot.crop_name.trim()) {
    return landplot.crop_name.trim();
  }
  const name = (landplot.name || "").trim();
  const match = name.match(/^(.+?) — [\d.,]+\s*ha$/i);
  return match ? match[1].trim() : name;
}

export function getPlotDisplayLabel(landplot) {
  if (!landplot) return "";
  const base = getPlotBaseName(landplot);
  const formattedHa = formatPlotNameHectares(landplot.area_h2);
  if (!base) {
    return formattedHa;
  }
  return `${base} — ${formattedHa}`;
}

export function getPlotMapLabel(landplot) {
  if (!landplot) return "";
  return `${getPlotDisplayLabel(landplot)} ha`;
}

export function getLandplotCentroid(landplot) {
  if (!landplot) return null;
  if (landplot.landplot_center && landplot.landplot_center.lat != null) {
    return {
      lat: Number(landplot.landplot_center.lat),
      lng: Number(landplot.landplot_center.lng),
    };
  }
  if (landplot.center && landplot.center.lat != null) {
    return {
      lat: Number(landplot.center.lat),
      lng: Number(landplot.center.lng),
    };
  }
  const ring = parseLandplotRing(landplot);
  if (!ring.length) return null;
  const lat =
    ring.reduce((sum, point) => sum + point.lat, 0) / ring.length;
  const lng =
    ring.reduce((sum, point) => sum + point.lng, 0) / ring.length;
  return { lat, lng };
}

/** Teste ponto-dentro-do-polígono (anel aberto ou fechado). */
export function pointInPolygon(point, polygonRing) {
  if (!point || !polygonRing || polygonRing.length < 3) return false;
  const x = point.lng;
  const y = point.lat;
  let inside = false;
  for (let i = 0, j = polygonRing.length - 1; i < polygonRing.length; j = i, i += 1) {
    const xi = polygonRing[i].lng;
    const yi = polygonRing[i].lat;
    const xj = polygonRing[j].lng;
    const yj = polygonRing[j].lat;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function landPlotsInsideRing(landPlots, polygonRing) {
  if (!Array.isArray(landPlots) || !polygonRing || polygonRing.length < 3) {
    return [];
  }
  return landPlots.filter((plot) => {
    const centroid = getLandplotCentroid(plot);
    return centroid ? pointInPolygon(centroid, polygonRing) : false;
  });
}

export function parseBoundaryGeoJsonRing(boundaryGeojson) {
  if (!boundaryGeojson || !boundaryGeojson.coordinates) return [];
  const ring = boundaryGeojson.coordinates[0];
  if (!Array.isArray(ring)) return [];
  return ring.map((coord) => ({
    lat: coord[1],
    lng: coord[0],
  }));
}

export function isGroupSubFarm(farm) {
  return Boolean(farm && farm.parent);
}

export function mergeLandPlotLists(...lists) {
  const byId = new Map();
  lists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((plot) => {
      if (!plot || plot.id == null) return;
      const existing = byId.get(plot.id);
      byId.set(plot.id, existing ? { ...existing, ...plot } : plot);
    });
  });
  return Array.from(byId.values());
}

export function resolveSubFarmIdForPlot(plot, groupFarms) {
  if (!plot || !Array.isArray(groupFarms)) return null;
  const subFarms = groupFarms.filter(isGroupSubFarm);
  if (plot.farm != null) {
    const assigned = subFarms.find(
      (farm) => String(farm.id) === String(plot.farm)
    );
    if (assigned) return assigned.id;
  }
  for (const farm of subFarms) {
    const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
    if (ring.length < 3) continue;
    const centroid = getLandplotCentroid(plot);
    if (centroid && pointInPolygon(centroid, ring)) {
      return farm.id;
    }
  }
  return null;
}

export function landPlotIdsForSubFarm(plots, subFarmId, groupFarms) {
  const subFarm = (groupFarms || []).find(
    (farm) => String(farm.id) === String(subFarmId)
  );
  if (!subFarm) return [];

  const list = Array.isArray(plots) ? plots : [];
  const ids = new Set();

  list.forEach((plot) => {
    if (String(plot.farm) === String(subFarmId)) {
      ids.add(plot.id);
    }
  });

  const ring = parseBoundaryGeoJsonRing(subFarm.boundary_geojson);
  if (ring.length >= 3) {
    landPlotsInsideRing(list, ring).forEach((plot) => ids.add(plot.id));
  }

  return Array.from(ids);
}

export function sumLandPlotAreaHa(landPlots, plotIds) {
  const idSet = new Set((plotIds || []).map(String));
  return (landPlots || []).reduce((total, plot) => {
    if (!idSet.has(String(plot.id))) return total;
    const value = Number(String(plot.area_h2 || "").replace(",", "."));
    return total + (Number.isFinite(value) ? value : 0);
  }, 0);
}

function parsePlotAreaHa(plot) {
  const value = Number(String((plot && plot.area_h2) || "").replace(",", "."));
  return Number.isFinite(value) ? value : 0;
}

export function computeRingCentroid(ring) {
  const points = ringToEditablePoints(ring);
  if (!points.length) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  };
}

/** Aproximação visual/métrica de anel interno para cerca limpa (m). */
export function insetRingTowardCentroid(ring, widthM, maps) {
  const points = ringToEditablePoints(ring);
  if (points.length < 3 || !widthM || widthM <= 0) return points;

  const centroid = computeRingCentroid(points);
  if (!centroid) return points;

  const perimeterM = computePointsPerimeterM(closePolygonRing(points), maps);
  const avgHalfWidth = perimeterM / Math.max(8, points.length * 2);
  const insetRatio = Math.max(
    0.05,
    Math.min(0.95, 1 - widthM / Math.max(avgHalfWidth, widthM))
  );

  return points.map((point) => ({
    lat: centroid.lat + (point.lat - centroid.lat) * insetRatio,
    lng: centroid.lng + (point.lng - centroid.lng) * insetRatio,
  }));
}

/** Aproximação visual de anel externo para faixa de cerca limpa (m). */
export function outsetRingFromCentroid(ring, widthM, maps) {
  const points = ringToEditablePoints(ring);
  if (points.length < 3 || !widthM || widthM <= 0) return points;

  const centroid = computeRingCentroid(points);
  if (!centroid) return points;

  const perimeterM = computePointsPerimeterM(closePolygonRing(points), maps);
  const avgHalfWidth = perimeterM / Math.max(8, points.length * 2);
  const outsetRatio = Math.max(
    1.05,
    Math.min(2, 1 + widthM / Math.max(avgHalfWidth, widthM))
  );

  return points.map((point) => ({
    lat: centroid.lat + (point.lat - centroid.lat) * outsetRatio,
    lng: centroid.lng + (point.lng - centroid.lng) * outsetRatio,
  }));
}

/**
 * Área de trabalho = talhão completo. Faixa de cerca limpa é somada fora do
 * perímetro (não reduz a área interna). totalHa = workHa + fenceHa.
 */
export function computeCleanFenceSplit(ring, widthM, totalHa, maps) {
  const workHa = Number(totalHa) || 0;
  const width = Number(widthM) || 0;
  const perimeterM = computePointsPerimeterM(closePolygonRing(ring), maps);

  if (!width || width <= 0 || workHa <= 0 || ring.length < 3) {
    return {
      perimeterM,
      workHa,
      fenceHa: 0,
      totalHa: workHa,
      outerRing: ringToEditablePoints(ring),
    };
  }

  const fenceHa = (perimeterM * width) / 10000;
  const outerRing = outsetRingFromCentroid(ring, width, maps);

  return {
    perimeterM,
    workHa,
    fenceHa,
    totalHa: workHa + fenceHa,
    outerRing,
  };
}

export function summarizeCleanFenceSelection(landPlots, plotIds, widthM, maps) {
  const idSet = new Set((plotIds || []).map(String));
  let workHa = 0;
  let fenceHa = 0;
  let perimeterM = 0;
  const plots = [];

  (landPlots || []).forEach((plot) => {
    if (!idSet.has(String(plot.id))) return;
    const ring = parseLandplotRing(plot);
    const plotTotalHa = parsePlotAreaHa(plot);
    const split = computeCleanFenceSplit(ring, widthM, plotTotalHa, maps);
    workHa += split.workHa;
    fenceHa += split.fenceHa;
    perimeterM += split.perimeterM;
    plots.push({
      plotId: plot.id,
      name: plot.name,
      ...split,
    });
  });

  const cleanFenceWidthM = Number(widthM) || 0;
  const totalHa = workHa + fenceHa;

  return {
    count: plots.length,
    plotCount: plots.length,
    totalHa,
    workHa,
    fenceHa,
    perimeterM,
    cleanFenceWidthM,
    plots,
  };
}

const MANUAL_PIECE_COLORS = [
  { stroke: "#1565C0", fill: "#42A5F5" },
  { stroke: "#2E7D32", fill: "#66BB6A" },
  { stroke: "#EF6C00", fill: "#FFB74D" },
  { stroke: "#6A1B9A", fill: "#BA68C8" },
  { stroke: "#C62828", fill: "#E57373" },
  { stroke: "#00838F", fill: "#4DD0E1" },
];

export function manualPieceColor(index) {
  return MANUAL_PIECE_COLORS[index % MANUAL_PIECE_COLORS.length];
}

export function summarizeManualApplicationPieces(manualPieces) {
  const pieces = Array.isArray(manualPieces) ? manualPieces : [];
  const manualHa = pieces.reduce(
    (sum, piece) => sum + (Number(piece.areaHa) || 0),
    0
  );
  const manualPerimeterM = pieces.reduce(
    (sum, piece) => sum + (Number(piece.perimeterM) || 0),
    0
  );
  return {
    manualPieces: pieces,
    manualPieceCount: pieces.length,
    manualHa,
    manualPerimeterM,
  };
}

export function mergeApplicationAreaSummary(baseSummary, manualPieces) {
  const manual = summarizeManualApplicationPieces(manualPieces);
  const workHa = Number(baseSummary && baseSummary.workHa) || 0;
  const fenceHa = Number(baseSummary && baseSummary.fenceHa) || 0;
  const plotCount = Number(baseSummary && baseSummary.plotCount) || 0;
  const cleanFenceWidthM =
    Number(baseSummary && baseSummary.cleanFenceWidthM) || 0;

  return {
    ...(baseSummary || {}),
    ...manual,
    workHa,
    fenceHa,
    plotCount,
    cleanFenceWidthM,
    totalHa: workHa + fenceHa + manual.manualHa,
  };
}

export function manualPiecesFromPlanApplication(app) {
  if (!app || !Array.isArray(app.manual_pieces)) return [];
  return app.manual_pieces.map((piece, index) => ({
    id: piece.id || `manual-plan-${index}`,
    label: piece.label || `Trecho ${index + 1}`,
    areaHa: Number(piece.area_ha) || 0,
    perimeterM: Number(piece.perimeter_m) || 0,
    polygonWkt: piece.polygon_wkt || "",
    points: Array.isArray(piece.points) ? piece.points : [],
  }));
}

export function manualPiecesToPlanPayload(manualPieces) {
  return (manualPieces || []).map((piece, index) => ({
    id: piece.id || `manual-${index + 1}`,
    label: piece.label || `Trecho ${index + 1}`,
    area_ha: Number(piece.areaHa) || 0,
    perimeter_m: Number(piece.perimeterM) || 0,
    polygon_wkt: piece.polygonWkt || "",
  }));
}
